import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, Save, Send, Sparkles, Handshake, Layers,
  MessageSquare, SlidersHorizontal, Palette, Check, Plus, Trash2, Copy,
  Globe, Download, Upload, Image as ImageIcon, Loader2,
  GripVertical, ChevronUp, ChevronDown,
  FlaskConical, Monitor, Tablet, Smartphone, Pencil, Inbox, Mail, Code2,
  Plug, ShoppingBag, CreditCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, uploadImage } from "@/lib/api";
import SiteRenderer from "@/components/SiteRenderer";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const FONTS = ["Cabinet Grotesk", "Poppins", "Playfair Display", "Space Grotesk"];

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

const NEW_SECTION = {
  hero: { eyebrow: "NEW", heading: "Your headline here", subheading: "A short supporting line about your offer.", ctaText: "Get started", secondaryCtaText: "Learn more", imageQuery: "abstract gradient" },
  features: { title: "What we offer", subtitle: "Highlights", items: [{ title: "Feature one", description: "Describe the benefit." }, { title: "Feature two", description: "Describe the benefit." }, { title: "Feature three", description: "Describe the benefit." }] },
  about: { title: "About us", text: "Tell your story here.", imageQuery: "team working office" },
  stats: { items: [{ value: "100+", label: "Customers" }, { value: "4.9", label: "Rating" }, { value: "24/7", label: "Support" }] },
  gallery: { title: "Gallery", imageQueries: ["photo one", "photo two", "photo three"] },
  testimonials: { title: "What people say", items: [{ quote: "Absolutely brilliant.", name: "Happy Customer", role: "Verified buyer" }] },
  pricing: { title: "Pricing", plans: [{ name: "Basic", price: "$0", period: "/mo", features: ["Feature A", "Feature B"], ctaText: "Choose" }, { name: "Pro", price: "$19", period: "/mo", features: ["Everything in Basic", "More power"], ctaText: "Choose" }] },
  products: { title: "Shop", subtitle: "Best sellers", currency: "$", items: [{ name: "Product name", price: "0.00", description: "Short description.", imageQuery: "product photo", ctaText: "Add to cart" }] },
  cta: { heading: "Ready to get started?", subheading: "Join us today.", ctaText: "Get started" },
  contact: { title: "Get in touch", subtitle: "We'd love to hear from you", email: "hello@example.com", phone: "", address: "" },
  footer: { brand: "Brand", text: "Your tagline here.", links: ["Home", "About", "Contact"] },
};
const ADDABLE = ["hero", "features", "about", "stats", "gallery", "testimonials", "pricing", "products", "cta", "contact", "footer"];

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isBuilder = !!user?.features?.builder;
  const isAdmin = isBuilder;

  const [project, setProject] = useState(null);
  const [schema, setSchema] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("chat");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [thinking, setThinking] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [leads, setLeads] = useState([]);
  const [codeMsgs, setCodeMsgs] = useState([]);
  const [codeInput, setCodeInput] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const chatEndRef = useRef(null);

  const loadMedia = useCallback(() => {
    api.get(`/projects/${id}/media`).then((r) => setMedia(r.data.files)).catch(() => {});
  }, [id]);

  const loadLeads = useCallback(() => {
    api.get(`/projects/${id}/leads`).then((r) => setLeads(r.data.leads)).catch(() => {});
  }, [id]);

  const loadCodeChat = useCallback(() => {
    api.get(`/projects/${id}/code-assist`).then((r) => setCodeMsgs(r.data.messages || [])).catch(() => {});
  }, [id]);

  const sendCode = async () => {
    const t = codeInput.trim();
    if (!t || codeBusy) return;
    setCodeInput("");
    setCodeMsgs((m) => [...m, { role: "user", content: t }]);
    setCodeBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/code-assist`, { message: t });
      setCodeMsgs((m) => [...m, { role: "assistant", content: data.reply, html: data.html, title: data.title }]);
    } catch (e) {
      setCodeMsgs((m) => [...m, { role: "assistant", content: formatApiError(e.response?.data?.detail) || "Coding agent failed." }]);
    } finally {
      setCodeBusy(false);
    }
  };

  const injectCode = (html, title) => {
    mutate((s) => {
      const sec = { id: newId(), type: "custom", content: { html, title: title || "Custom code" } };
      const fi = s.sections.findIndex((x) => x.type === "footer");
      if (fi >= 0) s.sections.splice(fi, 0, sec); else s.sections.push(sec);
    });
    toast.success(`Added "${title || "custom code"}" to your site`);
  };

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => {
      setProject(r.data.project);
      setSchema(r.data.project.schema_data);
    }).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail) || "Cannot open project");
      nav("/dashboard");
    });
    api.get(`/projects/${id}/chat`).then((r) => setMessages(r.data.messages)).catch(() => {});
    loadMedia();
  }, [id]); // eslint-disable-line

  const doUpload = async (file, onDone) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const res = await uploadImage(id, file);
      loadMedia();
      toast.success("Image uploaded");
      if (onDone) onDone(res.url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Handle return from Stripe handoff payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("handoff_session");
    if (!sid) return;
    let attempts = 0;
    let stop = false;
    const clean = () => window.history.replaceState({}, "", `/editor/${id}`);
    const poll = async () => {
      if (stop) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          const r = await api.get(`/projects/${id}`);
          setProject(r.data.project);
          setSchema(r.data.project.schema_data);
          clean();
          setAssignOpen(true);
          toast.success("Handoff payment received — enter your client's details.");
          return;
        }
      } catch { /* keep polling */ }
      if (attempts < 6) setTimeout(poll, 2000);
      else clean();
    };
    poll();
    return () => { stop = true; };
  }, [id]); // eslint-disable-line

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, thinking]);

  const save = useCallback(async (silent) => {
    if (!schema) return;
    setSaving(true);
    try {
      await api.put(`/projects/${id}`, { schema_data: schema });
      setDirty(false);
      if (!silent) toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, [id, schema]);

  // autosave
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => save(true), 900);
    return () => clearTimeout(t);
  }, [schema, dirty, save]);

  const mutate = (fn) => {
    setSchema((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      fn(copy);
      return copy;
    });
    setDirty(true);
  };

  const updateContent = (newContent) => mutate((s) => { s.sections[selected].content = newContent; });
  const updateMeta = (key, value) => mutate((s) => { s.meta[key] = value; });

  const [dragIdx, setDragIdx] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const moveSection = (from, to) => {
    if (to < 0 || to >= schema.sections.length) return;
    mutate((s) => { const [x] = s.sections.splice(from, 1); s.sections.splice(to, 0, x); });
    setSelected(to);
  };
  const addSection = (type) => {
    setShowAdd(false);
    mutate((s) => { s.sections.push({ id: newId(), type, content: JSON.parse(JSON.stringify(NEW_SECTION[type])) }); });
    toast.success(`Added ${type} section`);
  };
  const deleteSection = (idx) => {
    mutate((s) => { s.sections.splice(idx, 1); });
    setSelected(null);
  };
  const duplicateSection = (idx) => {
    mutate((s) => { const c = JSON.parse(JSON.stringify(s.sections[idx])); c.id = newId(); s.sections.splice(idx + 1, 0, c); });
    toast.success("Section duplicated");
  };

  const sendChat = async (msgArg) => {
    const msg = ((msgArg ?? chatInput) || "").trim();
    if (!msg || thinking) return;
    if (msgArg == null) setChatInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setThinking(true);
    try {
      const { data } = await api.post(`/projects/${id}/chat`, { message: msg, origin_url: window.location.origin });
      setSchema(data.project.schema_data);
      setProject(data.project);
      setDirty(false);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ " + (formatApiError(e.response?.data?.detail) || "Something went wrong.") }]);
    } finally {
      setThinking(false);
    }
  };

  if (!schema) return <div className="min-h-screen flex items-center justify-center bg-[#f2f0eb] label-tech animate-pulse">Loading studio…</div>;

  const selSection = selected != null ? schema.sections[selected] : null;

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-[#f2f0eb]">
      {/* top bar */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-[#d3d3d3] bg-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="back-btn" onClick={() => nav("/dashboard")} className="border border-[#0a2540] p-2 hover:bg-[#0a2540] hover:text-white transition-colors"><ArrowLeft size={16} /></button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 bg-[#ff3b00] flex items-center justify-center text-white shrink-0"><Layers size={13} /></div>
            <input
              data-testid="project-name-input"
              value={project?.name || ""}
              onChange={(e) => setProject((p) => ({ ...p, name: e.target.value }))}
              onBlur={() => api.put(`/projects/${id}`, { name: project.name })}
              className="font-heading font-bold text-lg bg-transparent outline-none truncate max-w-[40vw]"
            />
          </div>
          <span className="label-tech hidden md:inline">{saving ? "SAVING…" : dirty ? "UNSAVED" : "SAVED"}</span>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button data-testid="handoff-btn" onClick={() => setAssignOpen(true)} className="border border-[#0a2540] px-3 py-2 flex items-center gap-2 text-sm font-semibold hover:bg-[#0a2540] hover:text-white transition-colors">
              <Handshake size={15} /> <span className="hidden sm:inline">{project?.customer_email ? "Handed off" : "Hand off"}</span>
            </button>
          )}
          <button data-testid="publish-btn" onClick={() => setPublishOpen(true)} className={`px-3 py-2 flex items-center gap-2 text-sm font-semibold transition-colors ${project?.published ? "bg-[#166534] text-white hover:bg-[#124f28]" : "border border-[#0a2540] hover:bg-[#0a2540] hover:text-white"}`}>
            <Globe size={15} /> <span className="hidden sm:inline">{project?.published ? "Published" : "Publish"}</span>
          </button>
          <button data-testid="test-mode-btn" onClick={() => { setTestMode((v) => !v); setSelected(null); }} className={`px-3 py-2 flex items-center gap-2 text-sm font-semibold transition-colors ${testMode ? "bg-[#0055ff] text-white hover:bg-[#0044cc]" : "border border-[#0055ff] text-[#0055ff] hover:bg-[#0055ff] hover:text-white"}`}>
            {testMode ? <Pencil size={15} /> : <FlaskConical size={15} />} <span className="hidden sm:inline">{testMode ? "Edit" : "Test"}</span>
          </button>
          <a data-testid="view-live-btn" href={`/preview/${id}`} target="_blank" rel="noreferrer" className="border border-[#0a2540] px-3 py-2 flex items-center gap-2 text-sm font-semibold hover:bg-[#0a2540] hover:text-white transition-colors"><ExternalLink size={15} /> <span className="hidden sm:inline">Live</span></a>
          <button data-testid="save-btn" onClick={() => save(false)} className="bg-[#ff3b00] text-white px-4 py-2 flex items-center gap-2 text-sm font-semibold hover:bg-[#e63500] transition-colors"><Save size={15} /> Save</button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {testMode ? (
          <TestSandbox
            schema={schema}
            projectId={id}
            device={device}
            setDevice={setDevice}
            sendChat={sendChat}
            thinking={thinking}
          />
        ) : (
        <>
        {/* left: sections */}
        <aside className="w-60 bg-white border-r border-[#d3d3d3] hidden md:flex flex-col shrink-0">
          <div className="flex items-center justify-between p-4 pb-2">
            <span className="label-tech">/ SECTIONS</span>
            <div className="relative">
              <button data-testid="add-section-btn" onClick={() => setShowAdd((v) => !v)} className="flex items-center gap-1 text-xs font-semibold border border-[#0a2540] px-2 py-1 hover:bg-[#0a2540] hover:text-white transition-colors">
                <Plus size={13} /> Add
              </button>
              {showAdd && (
                <div data-testid="add-section-menu" className="absolute right-0 top-8 z-30 bg-white border border-[#0a2540] shadow-hard w-40 max-h-72 overflow-auto">
                  {ADDABLE.map((t) => (
                    <button key={t} data-testid={`add-${t}`} onClick={() => addSection(t)} className="w-full text-left px-3 py-2 text-sm font-tech uppercase tracking-wider hover:bg-[#ff3b00]/10 hover:text-[#ff3b00]">{t}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="px-4 pb-2 text-[11px] text-[#475569]">Drag to reorder · hover for controls</p>
          <div className="flex-1 overflow-auto px-2 pb-4 space-y-1">
            {schema.sections.map((s, i) => (
              <div
                key={s.id || i}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null && dragIdx !== i) moveSection(dragIdx, i); setDragIdx(null); }}
                onDragEnd={() => setDragIdx(null)}
                data-testid={`section-${s.type}-${i}`}
                onClick={() => { setSelected(i); setTab("content"); }}
                className={`group flex items-center gap-1 pl-1 pr-1.5 py-2 text-sm font-tech uppercase tracking-wider border-l-2 cursor-pointer transition-colors ${selected === i ? "bg-[#ff3b00]/10 border-[#ff3b00] text-[#ff3b00]" : "border-transparent hover:bg-gray-50"} ${dragIdx === i ? "opacity-40" : ""}`}
              >
                <GripVertical size={14} className="text-gray-300 group-hover:text-gray-500 cursor-grab shrink-0" />
                <span className="flex-1 truncate">{s.type}</span>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`up-${i}`} onClick={(e) => { e.stopPropagation(); moveSection(i, i - 1); }} className="p-0.5 text-gray-400 hover:text-[#0a2540]" title="Move up"><ChevronUp size={13} /></button>
                  <button data-testid={`down-${i}`} onClick={(e) => { e.stopPropagation(); moveSection(i, i + 1); }} className="p-0.5 text-gray-400 hover:text-[#0a2540]" title="Move down"><ChevronDown size={13} /></button>
                  <button data-testid={`dup-${i}`} onClick={(e) => { e.stopPropagation(); duplicateSection(i); }} className="p-0.5 text-gray-400 hover:text-[#0055ff]" title="Duplicate"><Copy size={12} /></button>
                  <button data-testid={`del-${i}`} onClick={(e) => { e.stopPropagation(); deleteSection(i); }} className="p-0.5 text-gray-400 hover:text-[#ff3b00]" title="Delete"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* center: canvas */}
        <main className="flex-1 overflow-auto bg-[#e5e5e5] p-4 md:p-8" onClick={() => setSelected(null)}>
          <div className="max-w-4xl mx-auto bg-white shadow-2xl">
            <SiteRenderer schema={schema} editable onSelect={(i) => { setSelected(i); setTab("content"); }} selectedIndex={selected} />
          </div>
        </main>

        {/* right: panel */}
        <aside className="w-80 lg:w-96 bg-white border-l border-[#d3d3d3] flex flex-col shrink-0">
          <div className="flex border-b border-[#d3d3d3] shrink-0">
            <TabBtn active={tab === "chat"} onClick={() => setTab("chat")} icon={MessageSquare} label="Chat" testid="tab-chat" />
            <TabBtn active={tab === "content"} onClick={() => setTab("content")} icon={SlidersHorizontal} label="Content" testid="tab-content" />
            <TabBtn active={tab === "design"} onClick={() => setTab("design")} icon={Palette} label="Design" testid="tab-design" />
            <TabBtn active={tab === "media"} onClick={() => setTab("media")} icon={ImageIcon} label="Media" testid="tab-media" />
            <TabBtn active={tab === "leads"} onClick={() => { setTab("leads"); loadLeads(); }} icon={Inbox} label="Leads" testid="tab-leads" />
            <TabBtn active={tab === "code"} onClick={() => { setTab("code"); loadCodeChat(); }} icon={Code2} label="Code" testid="tab-code" />
            <TabBtn active={tab === "integrations"} onClick={() => setTab("integrations")} icon={Plug} label="Apps" testid="tab-integrations" />
          </div>

          {tab === "chat" && (
            <div className="flex-1 flex flex-col overflow-hidden bg-[#0a2540]">
              <div className="flex-1 overflow-auto p-4 space-y-3 font-tech text-sm">
                <div className="text-white/40">/ AI EDITOR — describe any change</div>
                {messages.length === 0 && (
                  <div className="text-white/50 space-y-1 mt-2">
                    <div className="text-[#0055ff]">Try:</div>
                    <div>· "change the headline to Welcome"</div>
                    <div>· "make the buttons green"</div>
                    <div>· "add a pricing section"</div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "text-white" : "text-[#8ab4ff]"}>
                    <span className="text-white/40">{m.role === "user" ? "you $ " : "ai > "}</span>{m.content}
                  </div>
                ))}
                {thinking && <div className="text-[#8ab4ff] animate-pulse">ai &gt; thinking…</div>}
                <div ref={chatEndRef} />
              </div>
              <div className="p-3 border-t border-white/10 flex gap-2 shrink-0">
                <input
                  data-testid="chat-input"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendChat()}
                  placeholder="Tell the AI what to change…"
                  className="flex-1 bg-white/5 border border-white/15 text-white font-tech text-sm p-2.5 outline-none focus:border-[#0055ff] placeholder:text-white/30"
                />
                <button data-testid="chat-send-btn" onClick={sendChat} disabled={thinking} className="bg-[#0055ff] text-white px-3 disabled:opacity-50"><Send size={16} /></button>
              </div>
            </div>
          )}

          {tab === "content" && (
            <div className="flex-1 overflow-auto p-4">
              {!selSection ? (
                <div className="label-tech opacity-60 mt-4">Select a section on the canvas or from the left to edit its content.</div>
              ) : (
                <div>
                  <div className="label-tech mb-4">/ EDITING: {selSection.type}</div>
                  <ContentEditor content={selSection.content} onChange={updateContent} onUpload={doUpload} />
                </div>
              )}
            </div>
          )}

          {tab === "design" && (
            <div className="flex-1 overflow-auto p-4 space-y-5">
              <div className="label-tech">/ BRAND</div>
              <TextField label="Site title" value={schema.meta.title} onChange={(v) => updateMeta("title", v)} testid="design-title" />
              <TextField label="Tagline" value={schema.meta.tagline || ""} onChange={(v) => updateMeta("tagline", v)} testid="design-tagline" />
              <ColorField label="Primary color" value={schema.meta.primaryColor} onChange={(v) => updateMeta("primaryColor", v)} testid="design-primary" />
              <ColorField label="Accent color" value={schema.meta.accentColor} onChange={(v) => updateMeta("accentColor", v)} testid="design-accent" />
              <div>
                <label className="label-tech block mb-2">Heading font</label>
                <div className="grid grid-cols-2 gap-2">
                  {FONTS.map((f) => (
                    <button key={f} data-testid={`font-${f}`} onClick={() => updateMeta("font", f)} className={`text-left border p-2 text-sm ${schema.meta.font === f ? "border-[#ff3b00] bg-[#ff3b00]/5" : "border-[#d3d3d3]"}`} style={{ fontFamily: `"${f}", sans-serif` }}>
                      {schema.meta.font === f && <Check size={12} className="inline mr-1 text-[#ff3b00]" />}{f}
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-t border-[#d3d3d3] pt-5">
                <label className="label-tech block mb-2">Brand logo</label>
                {schema.meta.logoUrl ? (
                  <div className="flex items-center gap-3">
                    <img src={schema.meta.logoUrl} alt="logo" className="h-12 w-12 object-contain border border-[#d3d3d3] bg-white p-1" />
                    <button data-testid="remove-logo-btn" onClick={() => updateMeta("logoUrl", "")} className="text-sm font-tech text-[#ff3b00]">Remove</button>
                  </div>
                ) : (
                  <label data-testid="upload-logo-btn" className="cursor-pointer inline-flex items-center gap-2 border border-[#0a2540] px-3 py-2 text-sm font-semibold hover:bg-[#0a2540] hover:text-white transition-colors">
                    <Upload size={14} /> Upload logo
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => doUpload(e.target.files?.[0], (url) => updateMeta("logoUrl", url))} />
                  </label>
                )}
                <p className="text-xs text-[#475569] mt-2">Shown in the site navbar.</p>
              </div>
              <div className="border-t border-[#d3d3d3] pt-5">
                <div className="flex items-center justify-between mb-2">
                  <label className="label-tech">AI chat assistant</label>
                  <button
                    data-testid="toggle-chatbot"
                    onClick={() => mutate((s) => { s.meta.chatbot = { ...(s.meta.chatbot || {}), enabled: (s.meta.chatbot?.enabled === false) }; })}
                    className={`w-11 h-6 rounded-full relative transition-colors ${schema.meta.chatbot?.enabled === false ? "bg-gray-300" : "bg-[#166534]"}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${schema.meta.chatbot?.enabled === false ? "left-0.5" : "left-[22px]"}`} />
                  </button>
                </div>
                <p className="text-xs text-[#475569] mb-3">A floating AI chatbot on your site that answers visitor questions using your site's content.</p>
                {schema.meta.chatbot?.enabled !== false && (
                  <div className="space-y-3">
                    <TextField label="Assistant name" value={schema.meta.chatbot?.name || "Assistant"} onChange={(v) => mutate((s) => { s.meta.chatbot = { ...(s.meta.chatbot || {}), name: v }; })} testid="chatbot-name" />
                    <TextField label="Greeting message" value={schema.meta.chatbot?.greeting || "Hi! 👋 How can I help you today?"} onChange={(v) => mutate((s) => { s.meta.chatbot = { ...(s.meta.chatbot || {}), greeting: v }; })} testid="chatbot-greeting" multiline />
                    <TextField label="Sales goal (optional)" value={schema.meta.chatbot?.goal || ""} onChange={(v) => mutate((s) => { s.meta.chatbot = { ...(s.meta.chatbot || {}), goal: v }; })} testid="chatbot-goal" multiline />
                    <p className="text-xs text-[#475569] -mt-1">e.g. "Get visitors to book a free consultation" or "Upsell the Pro plan". The AI salesperson steers chats toward this goal and collects visitor contact details.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "media" && (
            <div className="flex-1 overflow-auto p-4" data-testid="media-panel">
              <label data-testid="media-upload-btn" className="cursor-pointer w-full mb-4 border-2 border-dashed border-[#0055ff] p-6 flex flex-col items-center justify-center gap-2 text-center hover:bg-[#0055ff]/5 transition-colors">
                {uploading ? <Loader2 className="animate-spin text-[#0055ff]" size={22} /> : <Upload className="text-[#0055ff]" size={22} />}
                <span className="text-sm font-semibold">{uploading ? "Uploading…" : "Upload logo, branding or storefront pics"}</span>
                <span className="label-tech">PNG · JPG · WEBP · max 8MB</span>
                <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => doUpload(e.target.files?.[0])} />
              </label>
              {media.length === 0 ? (
                <div className="label-tech opacity-60 text-center mt-6">No images yet. Upload your logo or photos to use across your site.</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {media.map((m) => (
                    <div key={m.file_id} data-testid={`media-item-${m.file_id}`} className="group relative border border-[#d3d3d3] bg-white">
                      <img src={m.url} alt={m.filename} className="w-full h-28 object-cover" />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button onClick={() => { navigator.clipboard.writeText(m.url); toast.success("Image URL copied"); }} className="bg-white text-[#0a2540] p-2" title="Copy URL"><Copy size={14} /></button>
                        <button onClick={async () => { await api.delete(`/media/${m.file_id}`); setMedia((x) => x.filter((f) => f.file_id !== m.file_id)); toast.success("Deleted"); }} className="bg-white text-[#ff3b00] p-2" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#475569] mt-4">Tip: to place an image in a section, open the <b>Content</b> tab and hit <b>Upload</b> on any image field.</p>
            </div>
          )}

          {tab === "leads" && (
            <div className="flex-1 overflow-auto p-4" data-testid="leads-panel">
              <div className="flex items-center justify-between mb-3">
                <span className="label-tech">/ CAPTURED LEADS</span>
                <button data-testid="refresh-leads" onClick={loadLeads} className="text-xs font-semibold border border-[#0a2540] px-2 py-1 hover:bg-[#0a2540] hover:text-white transition-colors">Refresh</button>
              </div>
              <p className="text-xs text-[#475569] mb-4">Visitors who left their details via your on-site AI salesperson.</p>
              {leads.length === 0 ? (
                <div className="label-tech opacity-60 text-center mt-6">No leads yet. When visitors share their details in the AI chat, they'll appear here.</div>
              ) : (
                <div className="space-y-2">
                  {leads.map((l) => (
                    <div key={l.id} data-testid={`lead-item-${l.id}`} className="border border-[#d3d3d3] bg-white p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm truncate">{l.name}</span>
                        <span className="label-tech opacity-60 shrink-0">{new Date(l.created_at).toLocaleDateString()}</span>
                      </div>
                      <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-sm text-[#0055ff] hover:underline mt-1"><Mail size={13} /> {l.email}</a>
                      {l.note && <p className="text-xs text-[#475569] mt-1.5">{l.note}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "code" && (
            <div className="flex-1 flex flex-col overflow-hidden" data-testid="code-panel">
              <div className="px-4 pt-4 pb-2">
                <span className="label-tech">/ CODE WITH AI</span>
                <p className="text-xs text-[#475569] mt-1">Ask a coding question, or describe a custom feature to add to your site (countdown, form, slider…). Powered by Claude.</p>
              </div>
              <div className="flex-1 overflow-auto px-4 space-y-3">
                {codeMsgs.length === 0 && <div className="label-tech opacity-60 mt-4">e.g. “add a countdown timer”, “make a WhatsApp chat button”, “how do I center a div?”</div>}
                {codeMsgs.map((m, i) => (
                  <div key={i} data-testid={`code-msg-${i}`} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
                    <div className={`inline-block max-w-[90%] px-3 py-2 whitespace-pre-wrap text-left ${m.role === "user" ? "bg-[#0a2540] text-white" : "bg-gray-100 text-[#0a2540]"}`}>{m.content}</div>
                    {m.role === "assistant" && m.html && (
                      <div className="mt-2 border border-[#0055ff] bg-white p-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="label-tech text-[#0055ff]">{m.title || "Custom code"}</span>
                          <button data-testid={`inject-code-${i}`} onClick={() => injectCode(m.html, m.title)} className="text-xs font-semibold bg-[#0055ff] text-white px-2 py-1 hover:bg-[#0044cc] transition-colors flex items-center gap-1"><Plus size={12} /> Add to site</button>
                        </div>
                        <pre className="text-[10px] bg-[#0a2540] text-green-300 p-2 overflow-auto max-h-40"><code>{m.html}</code></pre>
                      </div>
                    )}
                  </div>
                ))}
                {codeBusy && <div className="text-xs text-[#475569]">Claude is coding…</div>}
              </div>
              <div className="p-3 border-t border-[#d3d3d3] flex gap-2">
                <input data-testid="code-input" value={codeInput} onChange={(e) => setCodeInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendCode()} placeholder="Ask or describe a feature…" className="flex-1 border border-[#d3d3d3] px-3 py-2.5 text-sm outline-none focus:border-[#0055ff] font-tech" />
                <button data-testid="code-send" onClick={sendCode} disabled={codeBusy} className="bg-[#0055ff] text-white px-4 disabled:opacity-60 hover:bg-[#0044cc] transition-colors flex items-center gap-1">{codeBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}</button>
              </div>
            </div>
          )}

          {tab === "integrations" && (
            <div className="flex-1 overflow-auto p-4" data-testid="integrations-panel">
              <IntegrationsPanel schema={schema} mutate={mutate} />
            </div>
          )}
        </aside>
        </>
        )}
      </div>

      {isAdmin && <AssignDialog id={id} open={assignOpen} onOpenChange={setAssignOpen} project={project} onDone={setProject} freeHandoff={!!user?.features?.beta} />}
      <PublishDialog id={id} open={publishOpen} onOpenChange={setPublishOpen} project={project} onDone={setProject} />
    </div>
  );
}

function TestSandbox({ schema, projectId, device, setDevice, sendChat, thinking }) {
  const [iterInput, setIterInput] = useState("");
  const widths = { desktop: "100%", tablet: "820px", mobile: "390px" };
  const devices = [
    { id: "desktop", icon: Monitor, label: "Desktop" },
    { id: "tablet", icon: Tablet, label: "Tablet" },
    { id: "mobile", icon: Smartphone, label: "Mobile" },
  ];
  const runIterate = () => {
    const t = iterInput.trim();
    if (!t || thinking) return;
    setIterInput("");
    sendChat(t);
  };
  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-[#0a2540]" data-testid="test-sandbox">
      {/* sandbox toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#0a2540] border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2 text-white">
          <span className="flex items-center gap-1.5 bg-[#0055ff] px-2.5 py-1 label-tech" style={{ color: "#fff" }}>
            <FlaskConical size={13} /> LIVE TEST MODE
          </span>
          <span className="hidden md:inline text-white/50 text-xs">Interact with your site & AI salesperson exactly as a visitor would</span>
        </div>
        <div className="flex items-center gap-1 bg-white/5 p-1">
          {devices.map((d) => (
            <button
              key={d.id}
              data-testid={`device-${d.id}`}
              onClick={() => setDevice(d.id)}
              title={d.label}
              className={`p-1.5 transition-colors ${device === d.id ? "bg-[#0055ff] text-white" : "text-white/60 hover:text-white"}`}
            >
              <d.icon size={16} />
            </button>
          ))}
        </div>
      </div>

      {/* interactive preview */}
      <div className="flex-1 overflow-auto p-4 md:p-8 flex justify-center" style={{ background: "#1a2f47" }}>
        <div
          className="relative bg-white shadow-2xl transition-all duration-300 h-fit min-h-full"
          style={{ width: widths[device], maxWidth: "100%" }}
          data-testid="test-preview-frame"
        >
          <SiteRenderer schema={schema} projectId={projectId} sandbox />
        </div>
      </div>

      {/* live iterate bar */}
      <div className="shrink-0 px-4 py-3 bg-[#0a2540] border-t border-white/10 flex items-center gap-2">
        <Sparkles size={16} className="text-[#0055ff] shrink-0" />
        <input
          data-testid="test-ai-input"
          value={iterInput}
          onChange={(e) => setIterInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && runIterate()}
          placeholder="Iterate live — e.g. “make the hero headline bolder” or “regenerate the pricing section”"
          className="flex-1 bg-white/10 text-white placeholder:text-white/40 px-4 py-2.5 text-sm outline-none focus:bg-white/15 font-tech"
        />
        <button
          data-testid="test-ai-send"
          onClick={runIterate}
          disabled={thinking}
          className="bg-[#0055ff] text-white px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#0044cc] disabled:opacity-60 transition-colors"
        >
          {thinking ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {thinking ? "Updating…" : "Update"}
        </button>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-tech uppercase tracking-wider border-b-2 transition-colors ${active ? "border-[#ff3b00] text-[#ff3b00]" : "border-transparent text-gray-500 hover:text-black"}`}>
      <Icon size={16} /> {label}
    </button>
  );
}

function TextField({ label, value, onChange, testid, multiline }) {
  const long = multiline || (typeof value === "string" && value.length > 60);
  return (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      {long ? (
        <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full bg-white border border-[#d3d3d3] p-2.5 text-sm outline-none focus:border-[#ff3b00] resize-y" />
      ) : (
        <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-[#d3d3d3] p-2.5 text-sm outline-none focus:border-[#ff3b00]" />
      )}
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 border border-[#d3d3d3] cursor-pointer" />
        <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 bg-white border border-[#d3d3d3] p-2.5 text-sm font-tech outline-none focus:border-[#ff3b00]" />
      </div>
    </div>
  );
}

const humanize = (k) => k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).replace(/ Quer(y|ies)/, "").replace(/ ?Url/, "");
const isImgKey = (k) => /image|logo|photo|avatar|banner|picture|img/i.test(k);
const isUrl = (v) => typeof v === "string" && v.startsWith("http");

// Generic recursive content editor for a section's `content` object
function ContentEditor({ content, onChange, onUpload }) {
  const setKey = (key, val) => onChange({ ...content, [key]: val });

  return (
    <div className="space-y-4">
      {Object.entries(content).map(([key, val]) => {
        if (typeof val === "string") {
          if (isImgKey(key)) {
            return (
              <div key={key} className="space-y-1.5">
                <label className="label-tech block">{humanize(key)}</label>
                {isUrl(val) && <img src={val} alt="" className="h-24 w-full object-cover border border-[#d3d3d3]" />}
                <div className="flex gap-2">
                  <input value={val} onChange={(e) => setKey(key, e.target.value)} placeholder="keywords or image URL" className="flex-1 bg-white border border-[#d3d3d3] p-2.5 text-sm outline-none focus:border-[#ff3b00]" data-testid={`field-${key}`} />
                  <label data-testid={`upload-${key}`} className="cursor-pointer border border-[#0a2540] px-3 flex items-center hover:bg-[#0a2540] hover:text-white transition-colors" title="Upload image">
                    <Upload size={14} />
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0], (url) => setKey(key, url))} />
                  </label>
                </div>
              </div>
            );
          }
          return <TextField key={key} label={humanize(key)} value={val} onChange={(v) => setKey(key, v)} testid={`field-${key}`} />;
        }
        if (Array.isArray(val)) {
          const isStrArr = val.every((x) => typeof x === "string");
          const imgArr = isStrArr && isImgKey(key);
          return (
            <div key={key} className="border border-[#d3d3d3] p-3">
              <div className="label-tech mb-2">{humanize(key)}</div>
              <div className="space-y-3">
                {val.map((item, i) => (
                  <div key={i} className="relative border-l-2 border-[#ff3b00]/40 pl-3">
                    <button onClick={() => setKey(key, val.filter((_, k) => k !== i))} className="absolute right-0 top-0 text-gray-400 hover:text-[#ff3b00]"><Trash2 size={13} /></button>
                    {isStrArr ? (
                      <div className="space-y-1.5">
                        {imgArr && isUrl(item) && <img src={item} alt="" className="h-20 w-full object-cover border border-[#d3d3d3]" />}
                        <div className="flex gap-2">
                          <input value={item} onChange={(e) => { const n = [...val]; n[i] = e.target.value; setKey(key, n); }} className="flex-1 bg-white border border-[#d3d3d3] p-2 text-sm outline-none focus:border-[#ff3b00]" />
                          {imgArr && (
                            <label className="cursor-pointer border border-[#0a2540] px-3 flex items-center hover:bg-[#0a2540] hover:text-white transition-colors" title="Upload image">
                              <Upload size={13} />
                              <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0], (url) => { const n = [...val]; n[i] = url; setKey(key, n); })} />
                            </label>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(item).map(([ik, iv]) =>
                          typeof iv === "string" ? (
                            isImgKey(ik) ? (
                              <div key={ik} className="space-y-1.5">
                                <label className="label-tech block">{humanize(ik)}</label>
                                {isUrl(iv) && <img src={iv} alt="" className="h-20 w-full object-cover border border-[#d3d3d3]" />}
                                <div className="flex gap-2">
                                  <input value={iv} onChange={(e) => { const n = [...val]; n[i] = { ...n[i], [ik]: e.target.value }; setKey(key, n); }} className="flex-1 bg-white border border-[#d3d3d3] p-2 text-sm outline-none focus:border-[#ff3b00]" />
                                  <label className="cursor-pointer border border-[#0a2540] px-3 flex items-center hover:bg-[#0a2540] hover:text-white transition-colors" title="Upload image">
                                    <Upload size={13} />
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0], (url) => { const n = [...val]; n[i] = { ...n[i], [ik]: url }; setKey(key, n); })} />
                                  </label>
                                </div>
                              </div>
                            ) : (
                              <TextField key={ik} label={humanize(ik)} value={iv} onChange={(v) => { const n = [...val]; n[i] = { ...n[i], [ik]: v }; setKey(key, n); }} testid={`field-${key}-${i}-${ik}`} />
                            )
                          ) : Array.isArray(iv) ? (
                            <div key={ik}>
                              <div className="label-tech mb-1">{humanize(ik)}</div>
                              {iv.map((sv, si) => (
                                <input key={si} value={sv} onChange={(e) => { const n = JSON.parse(JSON.stringify(val)); n[i][ik][si] = e.target.value; setKey(key, n); }} className="w-full bg-white border border-[#d3d3d3] p-2 text-sm mb-1 outline-none focus:border-[#ff3b00]" />
                              ))}
                            </div>
                          ) : null
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => {
                    const template = isStrArr ? (imgArr ? "new photo" : "New item") : Object.fromEntries(Object.keys(val[0] || { title: "", description: "" }).map((k) => [k, Array.isArray(val[0]?.[k]) ? [] : ""]));
                    setKey(key, [...val, template]);
                  }}
                  className="flex items-center gap-1 text-xs font-tech text-[#0055ff] hover:underline"
                >
                  <Plus size={12} /> Add
                </button>
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function IntegrationsPanel({ schema, mutate }) {
  const integ = schema.meta.integrations || {};
  const setInteg = (k, v) => mutate((s) => { s.meta.integrations = { ...(s.meta.integrations || {}), [k]: v }; });
  const productSections = schema.sections.map((s, i) => ({ s, i })).filter((x) => x.s.type === "products");
  const setItem = (si, ii, patch) => mutate((s) => {
    const items = s.sections[si].content.items;
    items[ii] = { ...items[ii], ...patch };
  });

  return (
    <div className="space-y-6">
      <p className="text-xs text-[#475569] -mt-1">Connect your site to the tools your business already uses. These appear on the live site, test mode and exported HTML.</p>

      {/* Payments */}
      <div className="border border-[#0a2540] bg-white">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#d3d3d3] bg-[#0a2540] text-white">
          <CreditCard size={15} /> <span className="label-tech" style={{ color: "#fff" }}>PAYMENTS · STRIPE</span>
        </div>
        <div className="p-3 space-y-4">
          {productSections.length === 0 ? (
            <p className="text-xs text-[#475569]">Add a <b>Products</b> section (Sections → Add → products) to sell items. Each product's Buy button becomes a live Stripe checkout automatically once it has a price.</p>
          ) : (
            <>
              <p className="text-xs text-[#475569]">Every product with a price checks out through Stripe by default. Switch any product to an <b>affiliate link</b> to earn commissions instead.</p>
              {productSections.map(({ s, i }) => (
                <div key={i} className="border border-[#d3d3d3]">
                  <div className="label-tech px-2.5 py-1.5 bg-gray-50 border-b border-[#d3d3d3] flex items-center gap-1.5"><ShoppingBag size={12} /> {s.content.title || "Products"}</div>
                  <div className="p-2.5 space-y-3">
                    {(s.content.items || []).map((it, ii) => {
                      const mode = it.buyMode || "stripe";
                      return (
                        <div key={ii} className="border-l-2 border-[#ff3b00]/40 pl-2.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold truncate">{it.name || `Item ${ii + 1}`}</span>
                            <span className="label-tech opacity-60 shrink-0">{s.content.currency || "$"}{it.price}</span>
                          </div>
                          <div className="flex gap-1 mt-1.5">
                            <button data-testid={`buymode-stripe-${i}-${ii}`} onClick={() => setItem(i, ii, { buyMode: "stripe" })} className={`flex-1 text-xs font-semibold py-1.5 border ${mode !== "affiliate" ? "bg-[#0a2540] text-white border-[#0a2540]" : "border-[#d3d3d3] text-[#475569]"}`}>Stripe checkout</button>
                            <button data-testid={`buymode-affiliate-${i}-${ii}`} onClick={() => setItem(i, ii, { buyMode: "affiliate" })} className={`flex-1 text-xs font-semibold py-1.5 border ${mode === "affiliate" ? "bg-[#ff3b00] text-white border-[#ff3b00]" : "border-[#d3d3d3] text-[#475569]"}`}>Affiliate link</button>
                          </div>
                          {mode === "affiliate" && (
                            <input
                              data-testid={`affiliate-url-${i}-${ii}`}
                              value={it.affiliateUrl || ""}
                              onChange={(e) => setItem(i, ii, { affiliateUrl: e.target.value })}
                              placeholder="https://affiliate-link.com/…"
                              className="w-full mt-2 bg-white border border-[#d3d3d3] p-2 text-xs outline-none focus:border-[#ff3b00]"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-[#475569]">Stripe payments go to your connected Stripe account. Test the flow in <b>Live / Preview</b> — the Buy button opens a real Stripe checkout.</p>
            </>
          )}
        </div>
      </div>

      {/* Analytics */}
      <IntegSection title="Google Analytics" testid="integ-ga">
        <TextField label="GA4 Measurement ID" value={integ.googleAnalytics || ""} onChange={(v) => setInteg("googleAnalytics", v)} testid="integ-ga-id" />
        <p className="text-[11px] text-[#475569] mt-1">e.g. G-XXXXXXX — from Google Analytics → Admin → Data Streams. Tracks visitors on your live/exported site.</p>
      </IntegSection>

      {/* WhatsApp */}
      <IntegSection title="WhatsApp chat button" testid="integ-whatsapp">
        <TextField label="WhatsApp number (with country code)" value={integ.whatsapp || ""} onChange={(v) => setInteg("whatsapp", v)} testid="integ-whatsapp-number" />
        <TextField label="Prefilled message (optional)" value={integ.whatsappMessage || ""} onChange={(v) => setInteg("whatsappMessage", v)} testid="integ-whatsapp-msg" />
        <p className="text-[11px] text-[#475569] mt-1">Adds a floating WhatsApp button so visitors can message you instantly.</p>
      </IntegSection>

      {/* Calendly */}
      <IntegSection title="Calendly booking" testid="integ-calendly">
        <TextField label="Calendly link" value={integ.calendly || ""} onChange={(v) => setInteg("calendly", v)} testid="integ-calendly-url" />
        <p className="text-[11px] text-[#475569] mt-1">Adds a "Book a call" button that opens your Calendly scheduler.</p>
      </IntegSection>

      {/* Mailchimp */}
      <IntegSection title="Mailchimp email signup" testid="integ-mailchimp">
        <TextField label="Signup form action URL" value={integ.mailchimp || ""} onChange={(v) => setInteg("mailchimp", v)} testid="integ-mailchimp-url" />
        <p className="text-[11px] text-[#475569] mt-1">From Mailchimp → Audience → Signup forms → Embedded form. Copy the form's <code>action</code> URL. Adds a newsletter box near the footer.</p>
      </IntegSection>

      {/* Social */}
      <IntegSection title="Social links" testid="integ-social">
        <TextField label="Instagram URL" value={integ.instagram || ""} onChange={(v) => setInteg("instagram", v)} testid="integ-instagram" />
        <TextField label="Facebook URL" value={integ.facebook || ""} onChange={(v) => setInteg("facebook", v)} testid="integ-facebook" />
        <TextField label="X / Twitter URL" value={integ.twitter || ""} onChange={(v) => setInteg("twitter", v)} testid="integ-twitter" />
        <TextField label="LinkedIn URL" value={integ.linkedin || ""} onChange={(v) => setInteg("linkedin", v)} testid="integ-linkedin" />
        <TextField label="YouTube URL" value={integ.youtube || ""} onChange={(v) => setInteg("youtube", v)} testid="integ-youtube" />
        <p className="text-[11px] text-[#475569] mt-1">Shown as icon links near the footer.</p>
      </IntegSection>

      {/* Maps */}
      <IntegSection title="Google Maps" testid="integ-maps">
        <TextField label="Business address or Maps embed URL" value={integ.googleMaps || ""} onChange={(v) => setInteg("googleMaps", v)} testid="integ-maps-url" />
        <p className="text-[11px] text-[#475569] mt-1">Type an address (e.g. "350 5th Ave, New York") or paste a Maps embed URL. Adds a live map near the footer.</p>
      </IntegSection>
    </div>
  );
}

function IntegSection({ title, testid, children }) {
  return (
    <div className="border border-[#d3d3d3] bg-white p-3 space-y-3" data-testid={testid}>
      <div className="label-tech flex items-center gap-1.5"><Plug size={12} /> {title}</div>
      {children}
    </div>
  );
}

function PublishDialog({ id, open, onOpenChange, project, onDone }) {
  const [domain, setDomain] = useState(project?.custom_domain || "");
  const [busy, setBusy] = useState(false);
  const publicUrl = `${window.location.origin}/preview/${id}`;
  const embedSnippet = `<script src="${process.env.REACT_APP_BACKEND_URL}/api/public/projects/${id}/widget.js" async></script>`;

  useEffect(() => { setDomain(project?.custom_domain || ""); }, [project]);

  const togglePublish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/publish`, { published: !project?.published, custom_domain: domain });
      onDone(data.project);
      toast.success(data.project.published ? "Site published!" : "Site unpublished");
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const saveDomain = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/publish`, { custom_domain: domain });
      onDone(data.project);
      toast.success("Domain saved");
    } catch { toast.error("Failed"); }
  };

  const exportHtml = async () => {
    try {
      const res = await api.get(`/projects/${id}/export`, { params: { origin: window.location.origin }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project?.name || "site").replace(/\s+/g, "-").toLowerCase()}.html`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("HTML downloaded");
    } catch { toast.error("Export failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#f2f0eb] border border-[#0a2540] max-w-lg">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Globe size={18} /> Publish & export</DialogTitle></DialogHeader>
        <div className="space-y-5 mt-2">
          <div className="flex items-center justify-between bg-white border border-[#0a2540] p-4">
            <div>
              <div className="font-semibold">{project?.published ? "Live" : "Not published"}</div>
              <div className="text-xs text-[#475569]">{project?.published ? "Anyone with the link can view this site." : "Make this site publicly viewable."}</div>
            </div>
            <button data-testid="toggle-publish-btn" onClick={togglePublish} disabled={busy} className={`px-4 py-2 font-semibold text-sm text-white disabled:opacity-60 ${project?.published ? "bg-[#0a2540] hover:bg-[#ff3b00]" : "bg-[#166534] hover:bg-[#124f28]"} transition-colors`}>
              {project?.published ? "Unpublish" : "Publish now"}
            </button>
          </div>

          {project?.published && (
            <div>
              <label className="label-tech block mb-1.5">Public URL</label>
              <div className="flex gap-2">
                <input readOnly value={publicUrl} data-testid="public-url" className="flex-1 bg-white border border-[#d3d3d3] p-2.5 text-sm font-tech" />
                <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }} className="border border-[#0a2540] px-3 hover:bg-[#0a2540] hover:text-white transition-colors"><Copy size={15} /></button>
                <a href={publicUrl} target="_blank" rel="noreferrer" className="border border-[#0a2540] px-3 flex items-center hover:bg-[#0a2540] hover:text-white transition-colors"><ExternalLink size={15} /></a>
              </div>
            </div>
          )}

          <div>
            <label className="label-tech block mb-1.5">Custom domain</label>
            <div className="flex gap-2">
              <input data-testid="custom-domain-input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="www.yourclient.com" className="flex-1 bg-white border border-[#d3d3d3] p-2.5 text-sm outline-none focus:border-[#ff3b00]" />
              <button data-testid="save-domain-btn" onClick={saveDomain} className="border border-[#0a2540] px-4 text-sm font-semibold hover:bg-[#0a2540] hover:text-white transition-colors">Save</button>
            </div>
            <p className="text-xs text-[#475569] mt-2 leading-relaxed">To go live on your own domain, download the HTML export below and upload it to any host (Netlify, Vercel, your own server), then point your domain there. Automatic DNS connection is coming soon.</p>
          </div>

          <div className="border-t border-[#d3d3d3] pt-4">
            <button data-testid="export-html-btn" onClick={exportHtml} className="w-full bg-[#ff3b00] text-white font-semibold py-3 flex items-center justify-center gap-2 hover:bg-[#e63500] transition-colors">
              <Download size={16} /> Download site as HTML
            </button>
            <p className="text-xs text-[#475569] mt-2 text-center">A single self-contained .html file your client can host anywhere.</p>
          </div>

          <div className="border-t border-[#d3d3d3] pt-4">
            <label className="label-tech block mb-1.5">Embed the AI salesperson on any website</label>
            <p className="text-xs text-[#475569] mb-2 leading-relaxed">Paste this snippet before <code>&lt;/body&gt;</code> on any external site (WordPress, Wix, Shopify, plain HTML) to add your GLM AI salesperson + lead capture.</p>
            <div className="flex gap-2">
              <textarea readOnly value={embedSnippet} data-testid="embed-snippet" rows={2} className="flex-1 bg-white border border-[#d3d3d3] p-2.5 text-xs font-tech resize-none" />
              <button data-testid="copy-embed-btn" onClick={() => { navigator.clipboard.writeText(embedSnippet); toast.success("Embed snippet copied"); }} className="border border-[#0a2540] px-3 hover:bg-[#0a2540] hover:text-white transition-colors"><Copy size={15} /></button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ id, open, onOpenChange, project, onDone, freeHandoff }) {
  const [email, setEmail] = useState(project?.customer_email || "");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/assign`, { customer_email: email, customer_name: name });
      onDone(data.project);
      setResult(data);
      toast.success("Site handed off!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Handoff failed");
    } finally {
      setBusy(false);
    }
  };

  const payHandoff = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/handoff-checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start payment");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#f2f0eb] border border-[#0a2540]">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Handshake size={18} /> Hand off to a client</DialogTitle></DialogHeader>
        {!result && !project?.handoff_paid && !freeHandoff ? (
          <div className="space-y-4 mt-2" data-testid="handoff-paywall">
            <div className="bg-white border border-[#0a2540] shadow-hard p-6 text-center">
              <div className="label-tech mb-1">CLIENT HANDOFF</div>
              <div className="font-heading font-black text-5xl">$29.99</div>
              <p className="text-sm text-[#475569] mt-3">One-time fee to hand this site off to a client and give them their own login to maintain it themselves.</p>
            </div>
            <button data-testid="pay-handoff-btn" onClick={payHandoff} disabled={busy} className="w-full bg-[#ff3b00] text-white font-semibold py-3 hover:bg-[#e63500] disabled:opacity-60 transition-colors">{busy ? "Redirecting to checkout…" : "Pay $29.99 & continue"}</button>
            <p className="text-xs text-[#475569] text-center">Secure one-time payment via Stripe</p>
          </div>
        ) : !result ? (
          <div className="space-y-4 mt-2">
            <div className="bg-[#166534]/10 border border-[#166534] px-3 py-2 text-sm font-tech text-[#166534] flex items-center gap-2"><Check size={14} /> Handoff paid — enter your client's details</div>
            <p className="text-sm text-[#475569]">Give a customer access to maintain this site themselves. If they don't have an account yet, we'll create one and generate a temporary password.</p>
            <div>
              <label className="label-tech block mb-1.5">Customer email</label>
              <input data-testid="assign-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-white border border-[#d3d3d3] p-3 outline-none focus:border-[#ff3b00]" />
            </div>
            <div>
              <label className="label-tech block mb-1.5">Customer name (optional)</label>
              <input data-testid="assign-name-input" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-white border border-[#d3d3d3] p-3 outline-none focus:border-[#ff3b00]" />
            </div>
            <button data-testid="assign-submit-btn" onClick={submit} disabled={busy} className="w-full bg-[#ff3b00] text-white font-semibold py-3 hover:bg-[#e63500] disabled:opacity-60 transition-colors">{busy ? "Handing off…" : "Hand off site"}</button>
          </div>
        ) : (
          <div className="space-y-4 mt-2" data-testid="assign-result">
            <div className="bg-[#166534]/10 border border-[#166534] p-4">
              <div className="font-semibold flex items-center gap-2 text-[#166534]"><Check size={16} /> Site handed off to {result.customer_email}</div>
            </div>
            <div className="bg-white border border-[#0a2540] p-4 font-tech text-sm space-y-2">
              <div className="label-tech">CLIENT LOGIN</div>
              <div>email: {result.customer_email}</div>
              {result.temp_password ? (
                <div className="flex items-center gap-2">password: <span className="text-[#ff3b00]">{result.temp_password}</span>
                  <button onClick={() => { navigator.clipboard.writeText(`${result.customer_email} / ${result.temp_password}`); toast.success("Copied"); }} className="text-gray-400 hover:text-black"><Copy size={13} /></button>
                </div>
              ) : (
                <div className="text-[#475569]">Existing account — they use their own password.</div>
              )}
            </div>
            <p className="text-xs text-[#475569]">Share these credentials with your client. They can log in and edit the site via chat or the content panel.</p>
            <button onClick={() => onOpenChange(false)} className="w-full border border-[#0a2540] py-3 font-semibold hover:bg-[#0a2540] hover:text-white transition-colors">Done</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
